import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Typography, Tooltip, Link } from '@material-ui/core';

// CONSTANTS
import { ROUTE_DISCOVER_CHANNEL } from '@/constants/routes';

// UTILITY
import { getPlaceholderUrl } from '@/utility/content';
import getLink from '@/utility/routes';

// COMPONENTS
import RouterLink from '@/components/Link';

// STYLES
import { Container } from '../styles';

const SearchCardChannel = ({ className, data, link, analyticsLocation, ...props }) => {
  const [t] = useTranslation();

  const image = React.useMemo(
    () =>
      data.img ||
      getPlaceholderUrl({ title: data.name, width: 126, height: 72, bgColor: 'F5F6F7' }),
    [data.img],
  );

  const to = React.useMemo(
    () =>
      link ||
      getLink(ROUTE_DISCOVER_CHANNEL, {
        slug: data.slug || data.channel_id,
      }),
    [link, data.slug, data.channel_id],
  );

  const appNames = data.apps.slice(0, 3).map(app => app.name);
  const subAppNames =
    data.apps.length >= 3 ? data.apps.slice(3, data.apps.length).map(app => app.name) : [];

  return (
    <Container {...props} className={clsx('search-card', `search-card-channel`, className)}>
      <RouterLink className="search-card__link" to={to} location={analyticsLocation} />

      <div className="search-card__column-group">
        <div className="search-card__column">
          <div className="search-card__image search-card-channel__image">
            <img src={image} alt="app-logo" />
          </div>
        </div>

        <div className="search-card__column">
          <Typography variant="h5">{data.name}</Typography>
        </div>
      </div>
      <div className="search-card__column-group">
        <div className="search-card__column">
          {!!appNames.length && (
            <>
              <Typography
                className="p4 search-card__availability-title"
                variant="inherit"
                component="p"
                color="textSecondary"
              >
                {t('common.availableOn')}:
              </Typography>

              <Typography className="search-card__availabilities" variant="body2" component="p">
                {appNames.join(', ')}
                {!!subAppNames.length && (
                  <>
                    {', '}
                    <Tooltip title={subAppNames.join(', ')} interactive>
                      <Link className="search-card__link-more" variant="inherit">
                        {t('common.plusCountMore', { count: subAppNames.length })}{' '}
                      </Link>
                    </Tooltip>
                  </>
                )}
              </Typography>
            </>
          )}
        </div>
      </div>
    </Container>
  );
};

SearchCardChannel.defaultProps = {
  className: '',
  link: '',
};

SearchCardChannel.propTypes = {
  className: PropTypes.string,
  data: PropTypes.object.isRequired,
  link: PropTypes.string,
  analyticsLocation: PropTypes.string,
};

export default React.memo(SearchCardChannel);
